import React, { memo, FC } from "react";
import Choices from "../Choices";
import ReusableAccordian from "../ReusableAccordion";
import { Grow, Typography, Divider, TextField } from "@mui/material";
import { alumColor } from "./defaults";
import { cladColorImgs } from "../ExteriorClad/defaults";
import { RowLayout, ColLayout } from "../../util/accordianStyles";
import { Image, Transformation } from "cloudinary-react";
import useAluminumColorSelector from "./useAluminumColorSelector";
import PleaseNote from "../PleaseNote";
import { useIsReadOnlyContextProviderContext } from "../../context/IsReadOnlyContext";

const AluminumColorAccordian: FC = () => {
  const {
    customClad,
    customCladExt,
    variant,
    checkMark,
    disabledExt,
    disabledInt,
    customCladColorExterior,
    handleClick,
    isSplit,
    handleChange,
    aluminumColorExterior,
    aluminumColor,
    customCladColor,
    handleClickSplit,
  } = useAluminumColorSelector();
  const { readOnly } = useIsReadOnlyContextProviderContext();

  return (
    <ReusableAccordian
      accordionName="aluminumColor"
      heading="Aluminum Color"
      secondaryHeading="Select aluminum color"
      colOrRow="col"
    >
      {customClad || customCladExt ? (
        <PleaseNote
          notes={[
            "A shade variation in custom color is expected in production line.",
            "Please send color sample to our office with your quote number included. Production will not start until sample is recieved. Send to: 3111 Golf Course Dr. Ventura, CA 91301",
          ]}
        />
      ) : (
        <PleaseNote notes="Colors on screen may vary from end product" />
      )}

      <RowLayout>
        <ColLayout style={{ marginTop: 20, marginBottom: 0 }}>
          <ColLayout style={{ marginTop: 0 }}>
            <RowLayout style={{ marginTop: 0 }}>
              {isSplit && (
                <Typography style={{ marginRight: 10, fontWeight: "bold" }}>
                  Exterior:
                </Typography>
              )}
              {alumColor.map((state) => (
                <div
                  key={state.id}
                  onClick={() =>
                    disabledExt(state.label) || readOnly
                      ? null
                      : handleClick(state.label, `${state.type}Exterior`)
                  }
                >
                  <Choices
                    label={state.label}
                    variant={variant(state.label, `${state.type}Exterior`)}
                    checkMark={checkMark(state.label, `${state.type}Exterior`)}
                    disabled={disabledExt(state.label) || readOnly}
                  />
                </div>
              ))}
            </RowLayout>
            {customCladExt && (
              <TextField
                error={!customCladColorExterior ? true : false}
                helperText={
                  !customCladColorExterior ? "Please Enter a Custom Color" : ""
                }
                variant="outlined"
                margin="dense"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(event, "customCladColorExterior")
                }
                value={customCladColorExterior}
                disabled={readOnly}
              />
            )}
          </ColLayout>

          {isSplit && (
            <ColLayout>
              <RowLayout>
                <Typography style={{ marginRight: 10, fontWeight: "bold" }}>
                  Interior:
                </Typography>
                {alumColor.map((state) => (
                  <div
                    key={state.id}
                    onClick={() =>
                      disabledInt(state.label) || readOnly
                        ? null
                        : handleClick(state.label, state.type)
                    }
                  >
                    <Choices
                      label={state.label}
                      variant={variant(state.label, state.type)}
                      checkMark={checkMark(state.label, state.type)}
                      disabled={disabledInt(state.label) || readOnly}
                    />
                  </div>
                ))}
              </RowLayout>
              {customClad && (
                <TextField
                  error={!customCladColor ? true : false}
                  helperText={
                    !customCladColor ? "Please Enter a Custom Color" : ""
                  }
                  variant="outlined"
                  margin="dense"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(event, "customCladColor")
                  }
                  value={customCladColor}
                />
              )}
            </ColLayout>
          )}
        </ColLayout>

        <Divider
          orientation="vertical"
          flexItem
          style={{ marginLeft: 10, marginRight: 10 }}
        />

        <div
          onClick={() => (readOnly ? null : handleClickSplit("Split Finish"))}
        >
          <Choices
            label={"Split Finish"}
            variant={isSplit ? "filled" : "outlined"}
            checkMark={isSplit}
            disabled={readOnly}
          />
        </div>
      </RowLayout>

      <RowLayout style={{ justifyContent: "space-around" }}>
        {aluminumColorExterior && aluminumColorExterior !== aluminumColor && (
          <CladColorImg
            color={aluminumColorExterior}
            subtitle={isSplit ? "Exterior" : ""}
          />
        )}
        {isSplit && aluminumColor && (
          <CladColorImg
            color={aluminumColor}
            subtitle={isSplit ? "Interior" : ""}
          />
        )}
      </RowLayout>
    </ReusableAccordian>
  );
};

interface CladColorImgProps {
  color: string;
  subtitle: string;
}

const CladColorImg: FC<CladColorImgProps> = memo(({ color, subtitle }) => {
  if (!color) return null;

  const imageShown = cladColorImgs.find((itm) =>
    color === "Jet Black" ? itm.label === "Carbon Black" : itm.label === color,
  );

  if (!imageShown) return null;

  return (
    <ColLayout style={{ margin: 10 }} key={imageShown.label}>
      <Grow in={true}>
        <div>
          <Image
            cloudName="ag-millworks"
            secure="true"
            publicId={imageShown.imgLabel}
          >
            <Transformation width="200" height="200" radius="35" crop="scale" />
          </Image>
        </div>
      </Grow>
      <Typography variant="caption">{subtitle}</Typography>
    </ColLayout>
  );
});

export default memo(AluminumColorAccordian);
